import { DealVersion, Maturity, SecurityType } from '../../../types/deal.types';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { DealMaturities } from '../../../helpers/issue/maturity';
import { DealSummaryGridColumnName } from '../../types/column.types';
import { USTreasury } from '../../../types/treasuries.types';
import { USTreasuries } from '../../../helpers/treasuries';
import { formatNumber } from '@angular/common';

export interface TesValue {
  tes: number;
  initialTreasuryFeed: string;
  treasuryFeed: string;
}

export function maturityValueGetter(
  seriesId: string,
  field: string
): ({ data }: { data: DealVersion }) => SecurityType[] {
  return ({ data }: { data: DealVersion }): SecurityType[] => {
    if (data.maturities) {
      return data.maturities
        .filter((maturity: Maturity) => maturity.seriesId === seriesId)
        .reduce((acc: SecurityType[], maturity: Maturity) => {
          if (acc.indexOf(maturity[field]) === -1) {
            acc.push(maturity[field]);
          }
          return acc;
        }, []);
    } else {
      return [];
    }
  };
}

export const maturityTaxEqYldGetter = (mtr: number) => ({ data }): string => {
  const margTaxRate = mtr / 100 || 0;
  if (!data.yield) {
    return null;
  } else {
    if (mtr === 0) {
      return '-';
    } else if (mtr < 100 && mtr >= 1) {
      return (data.yield / (1 - margTaxRate)).toString();
    }
    return '-';
  }
};

export function taxEqSpreadGetter(treasuries: USTreasury[], taxEqYield) {
  return ({ data }) => {
    if (!taxEqYield || !treasuries) {
      return null;
    }
    const maturityYear = data.chartDate.year();

    const initialTreasury = USTreasuries.getTreasuryDataForYear(
      treasuries,
      maturityYear,
      data.tesData ? data.tesData.initialTesYear : undefined
    );
    const treasury = USTreasuries.getTreasuryDataForYear(
      treasuries,
      maturityYear,
      data.tesData ? data.tesData.tesYear : undefined
    );
    return treasury
      ? {
          tes: (taxEqYield - (treasury ? treasury.current.yield : 0)) * 100,
          initialTreasuryFeed: initialTreasury.securityId,
          treasuryFeed: treasury.securityId
        }
      : null;
  };
}

export function blackoutValueGetter(
  field: string,
  showBlackoutText = false,
  valueGetter?: ColDef['valueGetter']
) {
  return (params: ValueGetterParams) => {
    const { data, context = {} } = params;
    const finalShowBlackoutText =
      typeof context.showBlackoutText === 'boolean'
        ? context.showBlackoutText
        : showBlackoutText;

    if (DealMaturities.isClosedOrNotOfferedMaturity(data)) {
      return finalShowBlackoutText
        ? DealMaturities.getClosedOrNotOfferedMaturityInfo(data)
        : null;
    } else if (typeof valueGetter === 'string') {
      return data[valueGetter];
    } else if (typeof valueGetter === 'function') {
      return valueGetter(params);
    } else {
      return data[field];
    }
  };
}

export function comparedBidsValueGetter(
  comparedBidsColumnName: string,
  orionId: number,
  isTaxable: boolean
) {
  const field = getBidsCompareColumnName(comparedBidsColumnName, isTaxable);

  return (params: ValueGetterParams) => {
    const { data, context = {} } = params;
    const bids = context.bids();
    const bidMaturity = getBidMaturity(data, bids, orionId);

    const primaryValue = bidMaturity ? bidMaturity[field] : null;

    const additionalValue = isTaxable
      ? getAdditionalValueFromTaxableMaturity(bidMaturity, field)
      : getAdditionalValueFromNotTaxableMaturity(
          bidMaturity,
          params.context.getLocale()
        );
    const spread = getSpreadValueFromMaturity(isTaxable, bidMaturity);

    const heatMapInfo = isTaxable
      ? getHeatMapInfoFromTaxableMaturity(data, primaryValue)
      : getHeatMapInfoFromNotTaxableMaturity(bidMaturity, primaryValue);

    return {
      isTaxable,
      value:
        !!primaryValue && (!!primaryValue.spread || primaryValue.spread === 0)
          ? primaryValue.spread
          : primaryValue,
      additionalValue,
      spread,
      ...heatMapInfo
    };
  };
}

function getBidMaturity(
  dealMaturity: Maturity,
  bids,
  seniorManagerOrionId: number
) {
  const currentBid = bids.filter(
    (bid) => bid.seniorManager.orionId === seniorManagerOrionId
  )[0];
  return mapBidMaturitiesToBasicMaturity(dealMaturity, currentBid.maturities);
}

function mapBidMaturitiesToBasicMaturity(basicMaturity, bidMaturities) {
  const { chartDate } = basicMaturity;
  const result = bidMaturities.filter((m) => m.chartDate.isSame(chartDate));
  return result.length ? result[0] : undefined;
}

export function getAdditionalValueFromTaxableMaturity(
  bidMaturity,
  field: DealSummaryGridColumnName
) {
  if (field === DealSummaryGridColumnName.TaxableTsySpread) {
    return bidMaturity && DealMaturities.getTreasuryYear(bidMaturity);
  }
  return null;
}

export function getAdditionalValueFromNotTaxableMaturity(bidMaturity, locale) {
  const value = bidMaturity && bidMaturity.coupon;
  return value && formatNumber(value, locale, '1.3');
}

function getHeatMapInfoFromTaxableMaturity(
  dealMaturity: Maturity,
  primaryValue
) {
  return {
    maturityDate: dealMaturity.maturityDate as any,
    yield: null
  };
}

function getHeatMapInfoFromNotTaxableMaturity(bids: Maturity, primaryValue) {
  return {
    maturityDate:
      (primaryValue && primaryValue.maturityDate) ||
      (bids && bids.maturityDate),
    yield: (primaryValue && primaryValue.yield) || (bids && bids.yield)
  };
}

function getBidsCompareColumnName(
  comparedBidsColumnName,
  isSeriesTaxable
): DealSummaryGridColumnName {
  return comparedBidsColumnName === 'Spread'
    ? !isSeriesTaxable
      ? DealSummaryGridColumnName.BenchmarkSpread
      : DealSummaryGridColumnName.TaxableTsySpread
    : (DealSummaryGridColumnName[
        comparedBidsColumnName
      ] as DealSummaryGridColumnName);
}

function getSpreadValueFromMaturity(isTax, bidMat) {
  if (isTax) {
    return bidMat && (bidMat.taxableTsySpread || bidMat.taxableTreasurySpread);
  }
  return bidMat && !!bidMat['benchmarkSpread']
    ? bidMat['benchmarkSpread'].spread
    : null;
}
